import basestationRender from "@/assets/about/devices/basestation.webp?w=320;640;960&format=webp&as=meta";
import batterystatRender from "@/assets/about/devices/batterystat.webp?w=320;640;960&format=webp&as=meta";
import pirSensorRender from "@/assets/about/devices/pir-sensor.webp?w=320;640;960&format=webp&as=meta";
import plugInRender from "@/assets/about/devices/plug-in.webp?w=320;640;960&format=webp&as=meta";
import rfSwitchRender from "@/assets/about/devices/rf-switch.webp?w=320;640;960&format=webp&as=meta";
import somoIoRender from "@/assets/about/devices/somo-io.webp?w=320;640;960&format=webp&as=meta";
import { DEVICE_LINE_ART } from "@/components/about/deviceLineArt";
import { responsiveImage } from "@/lib/responsiveImage";
import {
  motion,
  type MotionValue,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { useRef } from "react";

// [left, top, width, aspect] of each trimmed render in the Figma "Products" frame,
// as % of their union; `line` is the drawing's frame as % of its render.
// The first three are the top row; each row reveals together, top row first.
const DEVICES = [
  {
    render: somoIoRender,
    drawing: DEVICE_LINE_ART.somoIo,
    box: [19.7, 0.08, 23.68, 1424 / 1018],
    line: [9.35, -1.49, 80.39, 93.97],
  },
  {
    render: batterystatRender,
    drawing: DEVICE_LINE_ART.batterystat,
    box: [57.19, 0, 26.49, 1331 / 872],
    line: [5.8, -1.31, 88.25, 95.4],
  },
  {
    render: pirSensorRender,
    drawing: DEVICE_LINE_ART.pirSensor,
    box: [87.93, 6.32, 9.79, 1359 / 1117],
    line: [18.55, -1.38, 66.25, 81.24],
  },
  {
    render: plugInRender,
    drawing: DEVICE_LINE_ART.plugIn,
    box: [0, 36.83, 28.35, 1474 / 1040],
    line: [11.47, -0.85, 77.47, 91.72],
  },
  {
    render: basestationRender,
    drawing: DEVICE_LINE_ART.basestation,
    box: [37.55, 39.58, 27.24, 1415 / 969],
    line: [9.24, -1.06, 81.44, 88.98],
  },
  {
    render: rfSwitchRender,
    drawing: DEVICE_LINE_ART.rfSwitch,
    box: [73.54, 42.37, 26.46, 1201 / 855],
    line: [10.85, -0.86, 78.83, 83.06],
  },
] as const;

const ROW_SIZE = 3;
// The bottom row starts this far into the scroll range, overlapping the top row's end.
const ROW_STAGGER = 0.4;
const SIZES = "(min-width: 1360px) 390px, (min-width: 640px) 28vw, 170px";

export function DeviceReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  // Pure line art until the composition's top passes mid-screen; the bottom
  // row finishes before it reaches the lower quarter.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.45", "end 0.75"],
  });
  return (
    <section aria-hidden className="py-8 sm:px-4 md:px-6 md:py-16">
      {/* Below sm the composition overhangs the viewport so devices stay
          legible; the outer ones crop at the edges. */}
      <div className="mx-auto max-w-8xl overflow-hidden sm:overflow-visible">
        <div
          ref={ref}
          className="relative -mx-8 sm:mx-0"
          style={{ aspectRatio: "8020 / 2622" }}
        >
          {DEVICES.map((device, index) => (
            <Device
              key={index}
              {...device}
              progress={scrollYProgress}
              start={Math.floor(index / ROW_SIZE) * ROW_STAGGER}
              still={reduceMotion !== false}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Device({
  render,
  drawing,
  box: [left, top, width, aspectRatio],
  line,
  progress,
  start,
  still,
}: (typeof DEVICES)[number] & {
  progress: MotionValue<number>;
  start: number;
  still: boolean;
}) {
  const renderOpacity = useTransform(
    progress,
    [start, start + 1 - ROW_STAGGER],
    [0, 1],
  );
  return (
    <div
      className="absolute"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        width: `${width}%`,
        aspectRatio,
      }}
    >
      <motion.img
        {...responsiveImage(render)}
        sizes={SIZES}
        loading="lazy"
        decoding="async"
        alt=""
        className="size-full"
        style={{ opacity: still ? 1 : renderOpacity }}
      />
      {!still && (
        <svg
          viewBox={`0 0 ${drawing.width} ${drawing.height}`}
          fill="none"
          className="absolute"
          style={{
            left: `${line[0]}%`,
            top: `${line[1]}%`,
            width: `${line[2]}%`,
            height: `${line[3]}%`,
          }}
        >
          {drawing.strokes.map((d, index) => (
            <Stroke
              key={index}
              d={d}
              erase={renderOpacity}
              start={(index / drawing.strokes.length) * (1 - STROKE_WINDOW)}
            />
          ))}
        </svg>
      )}
    </div>
  );
}

// Each stroke un-draws over this share of its device's reveal, staggered in
// path order like the home hero's line art draws in.
const STROKE_WINDOW = 0.3;

function Stroke({
  d,
  erase,
  start,
}: {
  d: string;
  erase: MotionValue<number>;
  start: number;
}) {
  const pathLength = useTransform(
    erase,
    [start, start + STROKE_WINDOW],
    [1, 0],
  );
  return (
    <motion.path
      d={d}
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      style={{ pathLength }}
    />
  );
}
